import { useQuery } from '@tanstack/react-query';
import type { ConfigIntegration } from '@overlayqa/shared';
import { configApi } from '../lib/api/config';

/**
 * Shown on an integration card when the server has NOT deployed that provider's
 * OAuth credentials. Honest and forward-looking: from the user's side the
 * integration simply isn't ready yet, and Connect would 500 with CONFIG_ERROR.
 */
export const INTEGRATION_UNAVAILABLE_MESSAGE =
  "This integration isn't available yet. We're finishing setup. Check back soon.";

export interface IntegrationAvailability {
  available: boolean;
  /** Non-null only when the provider is known to be unavailable. */
  reason: string | null;
}

/**
 * Whether <provider>'s export OAuth is actually deployed in THIS environment,
 * read from the server's resolved integration list (see lib/api/config.ts).
 *
 * Existing providers FAIL OPEN. New integrations can opt out until an older
 * server is upgraded to advertise them. For existing providers an unknown provider, a not-yet-loaded query, or a failed config
 * fetch all resolve to `available: true`. Only an explicit `enabled: false` from
 * the server (credentials absent) disables Connect. Disabling a provider that IS
 * deployed — e.g. on a transient config-fetch blip — would break a working
 * integration, which is strictly worse than the guaranteed-500 dead end this
 * gate closes. So the button is only ever taken away when we KNOW it can't work.
 */
export function useIntegrationAvailability(provider: string, availableBeforeServerSupport = true): IntegrationAvailability {
  const { data } = useQuery({
    queryKey: ['integration-availability'],
    queryFn: () => configApi.getExtensionConfig().then((c) => c.integrations),
    // Availability changes only on a server deploy — cache aggressively and
    // never refetch on focus. A failure leaves `data` undefined => fail open.
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const entry: ConfigIntegration | undefined = data?.find((i) => i.provider === provider);
  if (!entry && !availableBeforeServerSupport) return { available: false, reason: INTEGRATION_UNAVAILABLE_MESSAGE };
  if (!entry || entry.enabled) {
    return { available: true, reason: null };
  }
  return { available: false, reason: INTEGRATION_UNAVAILABLE_MESSAGE };
}
